import * as React from "react";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { styled, keyframes } from "@mui/material/styles";
import hero from "../assets/hero.webp";

// Keyframe animations for smooth text and image transitions
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const zoomIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.8);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

// Hero image container with striking visual effects
const StyledBox = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  alignSelf: "center",
  width: "70%",
  marginTop: theme.spacing(8),
  borderRadius: theme.shape.borderRadius,
  outline: "6px solid",
  outlineColor: "hsla(220, 25%, 80%, 0.2)",
  border: "1px solid",
  borderColor: theme.palette.grey[200],
  boxShadow: "0 0 12px 8px hsla(220, 25%, 80%, 0.2)",
  backgroundSize: "cover",
  overflow: "hidden",
  animation: `${zoomIn} 1.5s ease-out forwards`,
  [theme.breakpoints.up("sm")]: {
    marginTop: theme.spacing(10),
  },
  "& img": {
    width: "100%",
    height: "auto",
    transition: "transform 0.3s ease-in-out",
  },
  "&:hover img": {
    transform: "scale(1.05)",
  },
  ...theme.applyStyles("dark", {
    boxShadow: "0 0 24px 12px hsla(210, 100%, 25%, 0.2)",
    outlineColor: "hsla(220, 20%, 42%, 0.1)",
    borderColor: theme.palette.grey[700],
  }),
}));

export default function Hero() {
  return (
    <Box
      id="hero"
      sx={(theme) => ({
        width: "100%",
        backgroundRepeat: "no-repeat",
        backgroundImage:
          "radial-gradient(ellipse 80% 50% at 50% -20%, hsl(210, 100%, 90%), transparent)",
        ...theme.applyStyles("dark", {
          backgroundImage:
            "radial-gradient(ellipse 80% 50% at 50% -20%, hsl(210, 100%, 16%), transparent)",
        }),
      })}
    >
      <Container
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          pt: { xs: 14, sm: 20 },
          pb: { xs: 8, sm: 12 },
        }}
      >
        <Stack
          spacing={2}
          useFlexGap
          sx={{
            alignItems: "center",
            width: { xs: "100%" },
            animation: `${fadeInUp} 1s ease-out`,
          }}
        >
          <Typography
            variant="h1"
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              alignItems: "center",
              fontSize: "clamp(3rem, 10vw, 3.5rem)",
              textShadow: "2px 2px 6px rgba(0,0,0,0.3)",
            }}
          >
            Transforming&nbsp;
            <Typography
              component="span"
              variant="h1"
              sx={(theme) => ({
                fontSize: "inherit",
                color: "primary.main",
                textShadow: "2px 2px 6px rgba(0,0,0,0.3)",
                ...theme.applyStyles("dark", {
                  color: "primary.light",
                }),
              })}
            >
              IDEAS
            </Typography>
            &nbsp;into reality
          </Typography>
          <Typography
            variant="h4"
            color="text.secondary"
            sx={{
              mt: 2,
              textAlign: "center",
              textShadow: "1px 1px 4px rgba(0,0,0,0.2)",
            }}
          >
            Turning big ideas into groundbreaking digital realities.
          </Typography>
          <Typography
            sx={{
              textAlign: "center",
              color: "text.secondary",
              width: { sm: "100%", md: "80%" },
            }}
          >
            We deliver cutting-edge development solutions from India,
            collaborating with global and local organisations to drive
            innovation and transform businesses across private and public
            sectors.
          </Typography>
        </Stack>
        <StyledBox id="image">
          <img src={hero} alt="Hero" />
        </StyledBox>
      </Container>
    </Box>
  );
}
